import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Cloud, Check, Download, Copy, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [polling, setPolling] = useState(true);
  const sessionId = searchParams.get("session_id");
  const licenseKeyParam = searchParams.get("license_key");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    // Handle free orders immediately
    if (sessionId.startsWith("free_order_") && licenseKeyParam) {
      setPaymentData({
        payment_status: "paid",
        license_key: licenseKeyParam
      });
      setStatus("success");
      setPolling(false);
      toast.success("Order successful! Your license key is ready.");
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 2000;

    const pollStatus = async () => {
      if (attempts >= maxAttempts) {
        setPolling(false);
        toast.error("Payment verification timed out. Please contact support.");
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;
        setPaymentData(data);

        if (data.payment_status === "paid") {
          setStatus("success");
          setPolling(false);
          toast.success("Payment successful! Your license key is ready.");
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          setPolling(false);
          return;
        }

        attempts++;
        setTimeout(pollStatus, pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        attempts++;
        setTimeout(pollStatus, pollInterval);
      }
    };

    pollStatus();

    return () => {
      setPolling(false);
    };
  }, [sessionId, licenseKeyParam]);

  const copyLicenseKey = () => {
    if (paymentData?.license_key) {
      navigator.clipboard.writeText(paymentData.license_key);
      toast.success("License key copied to clipboard!");
    }
  };

  const downloadPlugin = () => {
    if (paymentData?.license_key) {
      window.location.href = `${API}/download/plugin/${paymentData.license_key}`;
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-12 pb-8 text-center">
            <Loader2 className="w-16 h-16 mx-auto text-bronze animate-spin mb-6" />
            <h2 className="text-2xl font-semibold text-midnight mb-2 font-outfit">
              Verifying Payment...
            </h2>
            <p className="text-slate-600">
              Please wait while we confirm your payment.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "error" || status === "expired") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
              <span className="text-3xl">!</span>
            </div>
            <h2 className="text-2xl font-semibold text-midnight mb-2 font-outfit">
              {status === "expired" ? "Session Expired" : "Something Went Wrong"}
            </h2>
            <p className="text-slate-600 mb-6">
              {status === "expired" 
                ? "Your payment session has expired. Please try again."
                : "We couldn't verify your payment. Please contact support."}
            </p>
            <Link to="/">
              <Button className="bg-midnight hover:bg-slate-800 text-white rounded-full">
                Return to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/" className="flex items-center gap-2">
            <Cloud className="w-8 h-8 text-bronze" />
            <span className="text-xl font-bold text-midnight font-outfit">CloudSync Pro</span>
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-6 animate-fade-in">
            <Check className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-midnight mb-2 font-outfit animate-fade-in-up">
            Payment Successful!
          </h1>
          <p className="text-lg text-slate-600 animate-fade-in-up animation-delay-100">
            Thank you for purchasing CloudSync Pro
          </p>
        </div>

        <Card className="mb-8 shadow-elevated animate-fade-in-up animation-delay-200" data-testid="success-card">
          <CardHeader>
            <CardTitle className="font-outfit">Your License Key</CardTitle>
            <CardDescription>
              Keep this key safe - you'll need it to activate the plugin
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-100 rounded-lg p-4 font-mono text-lg text-center mb-4 flex items-center justify-between">
              <span data-testid="license-key">{paymentData?.license_key || "Loading..."}</span>
              <Button 
                variant="ghost" 
                size="sm"
                data-testid="copy-license-btn"
                onClick={copyLicenseKey}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            <Button 
              data-testid="download-plugin-btn"
              onClick={downloadPlugin}
              className="w-full bg-bronze hover:bg-amber-600 text-white rounded-full py-6 text-lg"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Plugin
            </Button>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up animation-delay-300">
          <CardHeader>
            <CardTitle className="font-outfit">Next Steps</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-4">
              <li className="flex gap-4">
                <Badge className="w-8 h-8 rounded-full flex items-center justify-center bg-midnight text-white shrink-0">1</Badge>
                <div>
                  <h3 className="font-semibold text-midnight">Download the Plugin</h3>
                  <p className="text-slate-600 text-sm">Click the download button above to get your plugin ZIP file</p>
                </div>
              </li>
              <li className="flex gap-4">
                <Badge className="w-8 h-8 rounded-full flex items-center justify-center bg-midnight text-white shrink-0">2</Badge>
                <div>
                  <h3 className="font-semibold text-midnight">Install in WordPress</h3>
                  <p className="text-slate-600 text-sm">Go to Plugins → Add New → Upload Plugin in your WordPress admin</p>
                </div>
              </li>
              <li className="flex gap-4">
                <Badge className="w-8 h-8 rounded-full flex items-center justify-center bg-midnight text-white shrink-0">3</Badge>
                <div>
                  <h3 className="font-semibold text-midnight">Configure Cloudbeds</h3>
                  <p className="text-slate-600 text-sm">Enter your Cloudbeds API credentials in the plugin settings</p>
                </div>
              </li>
              <li className="flex gap-4">
                <Badge className="w-8 h-8 rounded-full flex items-center justify-center bg-midnight text-white shrink-0">4</Badge>
                <div>
                  <h3 className="font-semibold text-midnight">Start Syncing</h3>
                  <p className="text-slate-600 text-sm">Your rooms and availability will automatically sync with WooCommerce</p>
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <p className="text-slate-600 mb-4">
            Need help? Check our <a href="#" className="text-bronze hover:underline">documentation</a> or <a href="#" className="text-bronze hover:underline">contact support</a>
          </p>
          <Link to="/">
            <Button variant="outline" className="rounded-full">
              Return to Home
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
